// New Booking modal — staff-created festive pre-order.
// Wired to POST /festivals/:id/bookings via useCreateBooking.

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Button,
  Input,
  Select,
  useToast,
} from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { FestiveMenuItem } from '@/services/festive.service';
import { useCreateBooking, PAYMENT_METHOD_OPTIONS } from '@/features/bookings';
import type { BookingPaymentMethod } from '@/features/bookings';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';

interface LocationOption {
  id: string;
  name: string;
}

interface SelectedItem {
  product_id: string;
  product_name: string;
  unit_price: number; // paise
  quantity: number;
}

const PHONE_RE = /^\+[1-9]\d{6,14}$/;

export function NewBookingModal({
  open,
  onClose,
  festivalId,
  festivalName,
  advancePercentage,
  locations,
  defaultLocationId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  festivalId: string;
  festivalName: string;
  advancePercentage: number;
  locations: LocationOption[];
  defaultLocationId?: string;
  onCreated: () => void;
}) {
  const dial = useTenantDial();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const createBooking = useCreateBooking();

  const [menuItems, setMenuItems] = useState<FestiveMenuItem[]>([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [locationId, setLocationId] = useState(defaultLocationId ?? '');
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [slot, setSlot] = useState('');
  const [advance, setAdvance] = useState(''); // rupees
  const [paymentMethod, setPaymentMethod] = useState<BookingPaymentMethod>('CASH');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset + load the festival's bookable menu whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setName('');
    setPhone('');
    setLocationId(defaultLocationId ?? (locations.length === 1 ? locations[0].id : ''));
    setItems([]);
    setDeliveryDate('');
    setSlot('');
    setAdvance('');
    setPaymentMethod('CASH');
    setNotes('');
    setError(null);
    festiveService
      .getFestiveMenu(festivalId)
      .then((list) => setMenuItems(list.filter((m) => m.is_available)))
      .catch((err) =>
        toast({ title: err instanceof ApiError ? err.message : 'Failed to load menu items.', variant: 'error' }),
      );
  }, [open, festivalId, defaultLocationId, locations, toast]);

  const orderTotal = useMemo(
    () => items.reduce((s, i) => s + i.unit_price * i.quantity, 0),
    [items],
  );

  // Suggest the advance whenever the order total or % changes (only if untouched).
  const suggestedAdvance = useMemo(
    () => Math.round((orderTotal * advancePercentage) / 100),
    [orderTotal, advancePercentage],
  );

  const productOptions = useMemo(
    () =>
      menuItems
        .filter((m) => !items.some((i) => i.product_id === m.product_id))
        .map((m) => ({
          value: m.product_id,
          label: `${m.product_name} — ${formatPaise(m.festive_price, currency)}`,
        })),
    [menuItems, items, currency],
  );

  const addItem = useCallback(
    (productId: string) => {
      const m = menuItems.find((x) => x.product_id === productId);
      if (!m) return;
      setItems((prev) => [
        ...prev,
        { product_id: m.product_id, product_name: m.product_name, unit_price: m.festive_price, quantity: 1 },
      ]);
      setError(null);
    },
    [menuItems],
  );

  const setQty = useCallback((productId: string, qty: number) => {
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, qty) } : i)),
    );
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) { setError('Customer name is required'); return; }
    if (!PHONE_RE.test(phone)) { setError('Enter a valid phone number'); return; }
    if (!locationId) { setError('Select a location'); return; }
    if (items.length === 0) { setError('Add at least one item'); return; }
    if (!deliveryDate) { setError('Delivery / pickup date is required'); return; }

    const advancePaise = advance.trim()
      ? Math.round(parseFloat(advance) * getSubunitMultiplier(currency))
      : undefined;
    if (advancePaise != null && (isNaN(advancePaise) || advancePaise < 0)) {
      setError('Advance amount is invalid');
      return;
    }

    try {
      const result = await createBooking.mutateAsync({
        festivalId,
        payload: {
          customer_name: name.trim(),
          customer_phone: phone,
          location_id: locationId,
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          delivery_date: deliveryDate,
          delivery_slot: slot || undefined,
          advance_amount: advancePaise,
          payment_method: advancePaise ? paymentMethod : undefined,
          notes: notes.trim() || undefined,
        },
      });
      toast({ title: `Booking ${result.booking_number} created`, variant: 'success' });
      onClose();
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create booking');
    }
  }, [name, phone, locationId, items, deliveryDate, slot, advance, paymentMethod, notes, createBooking, festivalId, toast, onClose, onCreated]);

  const labelCls = 'text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block';

  return (
    <Modal open={open} onClose={onClose} title={`New Booking — ${festivalName}`} variant="wide">
      <div className="space-y-4 p-1 max-h-[72vh] overflow-y-auto">
        {error && (
          <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg" role="alert">
            {error}
          </div>
        )}

        {/* Customer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Customer Name *</label>
            <Input value={name} onChange={(v) => { setName(v); setError(null); }} placeholder="e.g. Priya Sharma" />
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <Input variant="phone" defaultDial={dial} value={phone} onChange={(v) => { setPhone(v); setError(null); }} placeholder="98765 43210" />
          </div>
        </div>

        <div>
          <label className={labelCls}>Location *</label>
          <Select
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
            value={locationId}
            onChange={(v) => { setLocationId(typeof v === 'string' ? v : v[0] ?? ''); setError(null); }}
            placeholder="Select a location"
          />
        </div>

        {/* Items */}
        <div>
          <label className={labelCls}>Add Item *</label>
          {menuItems.length === 0 ? (
            <p className="text-xs text-warning-600 bg-warning-50 dark:bg-warning-900/20 px-3 py-2 rounded-lg">
              This event has no available menu items yet. Add items via Festive Events → Manage Menu first.
            </p>
          ) : (
            <Select
              variant="searchable"
              options={productOptions}
              value=""
              onChange={(v) => addItem(typeof v === 'string' ? v : v[0] ?? '')}
              placeholder="Search and add a menu item"
            />
          )}

          {items.length > 0 && (
            <div className="mt-3 space-y-2">
              {items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate">{i.product_name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
                      {formatPaise(i.unit_price, currency)} each
                    </p>
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={i.quantity}
                    onChange={(e) => setQty(i.product_id, Number(e.target.value) || 1)}
                    className="w-16 px-2 py-1 rounded-lg bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                    aria-label={`Quantity for ${i.product_name}`}
                  />
                  <span className="w-24 text-right text-sm font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                    {formatPaise(i.unit_price * i.quantity, currency)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(i.product_id)}
                    className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                    aria-label={`Remove ${i.product_name}`}
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 pt-1">
                <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Order Total</span>
                <span className="text-base font-bold font-mono tabular-nums text-violet-700 dark:text-violet-400">
                  {formatPaise(orderTotal, currency)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Delivery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Delivery / Pickup Date *</label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => { setDeliveryDate(e.target.value); setError(null); }}
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 text-sm text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
            />
          </div>
          <div>
            <label className={labelCls}>Pickup Time</label>
            <input
              type="time"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 text-sm text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Advance */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>
              Advance Paid ({getCurrencySymbol(currency)})
              {orderTotal > 0 && (
                <button
                  type="button"
                  onClick={() => setAdvance((suggestedAdvance / getSubunitMultiplier(currency)).toString())}
                  className="ml-2 text-violet-600 dark:text-violet-400 hover:underline font-normal"
                >
                  Use {advancePercentage}% ({formatPaise(suggestedAdvance, currency)})
                </button>
              )}
            </label>
            <Input type="number" value={advance} onChange={(v) => setAdvance(v)} placeholder="0.00" />
          </div>
          <div>
            <label className={labelCls}>Payment Method</label>
            <Select
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentMethod}
              onChange={(v) => setPaymentMethod((typeof v === 'string' ? v : v[0]) as BookingPaymentMethod)}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Notes</label>
          <Input value={notes} onChange={(v) => setNotes(v)} placeholder="Special instructions (optional)" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={createBooking.isPending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={createBooking.isPending}>
            {createBooking.isPending ? 'Creating...' : 'Create Booking'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
